import { resolveApiBaseUrl, MOBILE_API_STORAGE_KEY } from '../config/apiConfig';

const ACCESS_TOKEN_KEY = 'gadkot_access_token';

/**
 * API base URL — see config/apiConfig.js (native app, env, local override).
 */
export function getBaseUrl() {
  return resolveApiBaseUrl();
}

export { MOBILE_API_STORAGE_KEY };

/** Build full URL for media/file paths returned by the backend (e.g. /api/media/files/xxx) */
export function getMediaFileUrl(path) {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const base = getBaseUrl().replace(/\/$/, '');
  return path.startsWith('/') ? `${base}${path}` : `${base}/${path}`;
}

export function getAccessToken() {
  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAccessToken(token) {
  try {
    if (!token) localStorage.removeItem(ACCESS_TOKEN_KEY);
    else localStorage.setItem(ACCESS_TOKEN_KEY, token);
  } catch {
    // ignore
  }
}

function buildQuery(query) {
  if (!query) return '';
  const params = new URLSearchParams();
  Object.entries(query).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    params.append(k, String(v));
  });
  const s = params.toString();
  return s ? `?${s}` : '';
}

async function parseJsonSafe(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

export async function apiRequest(path, { method = 'GET', query, body, headers, signal } = {}) {
  const baseUrl = getBaseUrl();
  const url = `${baseUrl}${path}${buildQuery(query)}`;

  const token = getAccessToken();
  const mergedHeaders = {
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };

  const hasBody = body !== undefined && body !== null;
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

  let requestBody = undefined;
  if (hasBody) {
    requestBody = isFormData ? body : JSON.stringify(body);
    if (!isFormData) mergedHeaders['Content-Type'] = mergedHeaders['Content-Type'] || 'application/json';
  }

  const res = await fetch(url, {
    method,
    headers: mergedHeaders,
    body: requestBody,
    signal
  });

  const json = await parseJsonSafe(res);

  if (!res.ok) {
    // Backend returns com.sandesh.forts.web.dto.ErrorResponse:
    // { timestamp, status, error, code, message, path }
    let msg = json?.message || res.statusText || 'Request failed';
    if (res.status === 403 && !token) {
      msg =
        'Sign in required. Please log in and try again.';
    } else if (res.status === 403 && (json?.code === 'FORBIDDEN' || json?.status === 403)) {
      msg = json?.message || 'Access denied. Try signing in again.';
    }
    if (res.status === 401) {
      msg = json?.message || 'Session expired or not signed in. Please log in again.';
    }
    if (res.status === 404 && (!json || json?.message === 'Not Found')) {
      const base = getBaseUrl();
      msg =
        base
          ? `API not found (404) at ${base}. Check the server is running and the URL is correct.`
          : 'API not found (404). Set backend URL in Mobile app settings or REACT_APP_API_BASE_URL.';
    }
    if (res.status === 500 && json?.code === 'INTERNAL_ERROR') {
      msg +=
        ' Check the Spring Boot server logs for the full stack trace (GlobalExceptionHandler logs unhandled exceptions).';
    }
    const err = new Error(msg);
    err.status = json?.status || res.status;
    err.code = json?.code || 'HTTP_ERROR';
    err.path = json?.path;
    err.response = json;
    throw err;
  }

  return json;
}

